/**
 * Umami, loaded only when both the script url and the website id are
 * configured.
 *
 * Neither value is a secret — both are compiled into the bundle and visible
 * in page source — so they are build-time variables. With either absent the
 * script is never injected and no request reaches the analytics host, which
 * is what keeps local development, CI and the test suite offline.
 *
 * The tracker is cookieless: it derives a daily identifier from a salt it
 * rotates every 24 hours, sets no cookie and writes nothing to storage. That
 * rotation is what removes the consent-banner requirement, and it is also
 * what makes a visitor unrecognisable across days. See
 * docs/adr/0010-visitor-analytics.md.
 */

const SCRIPT_ID = 'umami';

const src: string = import.meta.env.VITE_UMAMI_SRC ?? '';
const websiteId: string = import.meta.env.VITE_UMAMI_WEBSITE_ID ?? '';
const domains: string = import.meta.env.VITE_UMAMI_DOMAINS ?? '';

/**
 * Both values are required. A script url with no website id loads the tracker
 * and reports nothing; an id with no url is inert. Treating either alone as
 * "off" means a half-configured build behaves like an unconfigured one rather
 * than fetching a script for no reason.
 */
export const analyticsEnabled = src.length > 0 && websiteId.length > 0;

/**
 * Injects the tracker once. Idempotent, so a double invocation — which
 * StrictMode makes likely in development — does not load it twice.
 */
export function loadAnalytics(doc: Document = document): void {
  if (!analyticsEnabled) return;
  if (doc.getElementById(SCRIPT_ID)) return;

  const script = doc.createElement('script');
  script.id = SCRIPT_ID;
  script.src = src;
  script.defer = true;
  script.dataset.websiteId = websiteId;

  // Restricts reporting to the hosts named here. Without it a preview build,
  // or the production image run anywhere else, files its page views against
  // the same website id as the live site.
  if (domains.length > 0) script.dataset.domains = domains;

  doc.head.appendChild(script);
}
